import { CATEGORY_COLORS, CategoryType } from "@/pages/Dashboard";
import React, { useEffect, useRef } from "react";

// ---- Types ----
interface Location {
  lat: string | number;
  lng: string | number;
  id: string;
  category?: string;
  status?: string;
  imageUrl?: string;
  severity?: number;
}

interface PlacesSearchMapProps {
  coordinates?: any;
  setCoordinates?: any;
  isInput?: boolean;
  isDisabled?: boolean;
  locations?: Location[];
  height?: string;
  getMarkerColor?: (status?: string, severity?: number) => string;
  data?: Location[];
  onMarkerClick?: (location: Location) => void;
}


export default function PlacesSearchMap({
  coordinates,
  setCoordinates,
  isInput = true,
  isDisabled = false,
  locations = [],
  height = "500px",
  data,
  getMarkerColor,
  onMarkerClick,
}: PlacesSearchMapProps) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const autocompleteRef = useRef<HTMLInputElement | null>(null);

  const mapInstance = useRef<google.maps.Map | null>(null);
  const markers = useRef<google.maps.Marker[]>([]);
  const infoWindow = useRef<google.maps.InfoWindow | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);

  // -------- Helper: Get severity color ----------
  const getSeverityColor = (severity: number): string => {
    if (severity >= 8) return '#dc2626'; // Red for high severity
    if (severity >= 6) return '#ea580c'; // Orange for medium-high
    if (severity >= 4) return '#f59e0b'; // Yellow for medium
    return '#22c55e'; // Green for low severity
  };



  // -------- Add Multiple Markers ----------
  const addLocationMarkers = () => {
    const map = mapInstance.current;
    if (!map || data.length === 0) return;

    // Clear existing markers
    markers.current.forEach((marker) => marker.setMap(null));
    markers.current = [];

    const bounds = new google.maps.LatLngBounds();

    data.forEach((location) => {
      // Skip rejected status locations
      if (location.status === "REJECTED") {
        return;
      }

      if (location.lat && location.lng) {
        const lat =
          typeof location.lat === "string"
            ? parseFloat(location.lat)
            : location.lat;
        const lng =
          typeof location.lng === "string"
            ? parseFloat(location.lng)
            : location.lng;
        const position = { lat, lng };

        // ---- Decide icon ----
        let iconConfig;
        const categoryColor = location.category
          ? CATEGORY_COLORS[location.category]
          : undefined;

        if (categoryColor) {
          // Category-based circular marker
          iconConfig = {
            url:
              "data:image/svg+xml;charset=UTF-8," +
              encodeURIComponent(`
                <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                  {/* Marker shadow */}
                  <circle cx="18" cy="18" r="14" fill="#000000" opacity="0.2"/>
                  
                  {/* Main marker */}
                  <circle cx="16" cy="16" r="14" fill="${categoryColor}" stroke="#ffffff" strokeWidth="2"/>
                  
                  {/* Inner highlight */}
                  <circle cx="16" cy="16" r="10" fill="${categoryColor}" opacity="0.8"/>
                  
                  {/* Center dot */}
                  <circle cx="16" cy="16" r="6" fill="#ffffff"/>
                </svg>
              `),
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 16),
          };
        } else {
          // fallback marker with severity-based color
          const color = getMarkerColor ? getMarkerColor(location.status, location.severity) : getSeverityColor(location.severity || 5);
          iconConfig = {
            url:
              "data:image/svg+xml;charset=UTF-8," +
              encodeURIComponent(`
                <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                  {/* Marker shadow */}
                  <circle cx="18" cy="18" r="14" fill="#000000" opacity="0.2"/>
                  
                  {/* Main marker */}
                  <circle cx="16" cy="16" r="14" fill="${color}" stroke="#ffffff" strokeWidth="2"/>
                  
                  {/* Inner highlight */}
                  <circle cx="16" cy="16" r="10" fill="${color}" opacity="0.8"/>
                  
                  {/* Center dot */}
                  <circle cx="16" cy="16" r="6" fill="#ffffff"/>
                </svg>
              `),
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 16),
          };
        }

        const marker = new google.maps.Marker({
          position,
          map,
          title: `Issue ID: ${location.id} - Click to view details`,
          icon: iconConfig,
        });

        // Info window on hover
        marker.addListener("mouseover", () => {
          if (infoWindow.current) {
            const categoryColor = location.category ? CATEGORY_COLORS[location.category as CategoryType] : '#6b7280';
            
            // Set up global click handler for image clicks
            (window as any).handleImageClick = (issueId: string) => {
              if (onMarkerClick) {
                onMarkerClick(location);
              }
            };
            
            if (location.imageUrl) {
              // Just the image with overlaid elements - NO WHITE CONTAINER AT ALL
              infoWindow.current.setContent(`
                <style>
                  .gm-style .gm-style-iw-c { background: transparent !important; box-shadow: none !important; }
                  .gm-style .gm-style-iw-d { background: transparent !important; }
                  .gm-style .gm-style-iw-t::after { background: transparent !important; }
                  .gm-style .gm-style-iw { background: transparent !important; }
                </style>
                <div style="width: 320px; padding: 0; margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: transparent !important;">
                  <div style="position: relative; padding: 0; margin: 0; background: transparent !important;">
                    <div style="cursor: pointer; position: relative; background: rgba(243, 244, 246, 0.3); border-radius: 8px; min-height: 200px;" onclick="window.handleImageClick && window.handleImageClick('${location.id}')">
                      <img src="${location.imageUrl}" alt="Issue Image" style="width: 100%; height: 200px; object-fit: cover; display: block; border-radius: 8px; background: transparent;" onerror="this.style.display='none'" />
                    </div>
                    
                    <!-- Category Badge - Top Left -->
                    <div style="position: absolute; top: 12px; left: 12px; display: flex; align-items: center; gap: 6px; padding: 6px 10px; background: rgba(255, 255, 255, 0.95); border-radius: 16px; backdrop-filter: blur(8px); border: 1px solid rgba(255, 255, 255, 0.3); box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);">
                      <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${categoryColor}; border: 1px solid #ffffff;"></div>
                      <span style="font-size: 12px; font-weight: 600; color: #111827; text-transform: capitalize; white-space: nowrap;">
                        ${(location.category || "Unknown").replace(/_/g, ' ').toLowerCase()}
                      </span>
                    </div>
                    
                    <!-- Status Badge - Bottom Left -->
                    <div style="position: absolute; bottom: 12px; left: 12px; padding: 6px 12px; background: rgba(0, 0, 0, 0.8); border-radius: 16px; backdrop-filter: blur(8px); border: 1px solid rgba(255, 255, 255, 0.1); box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);">
                      <span style="font-size: 11px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap;">
                        ${location.status || "Unknown"}
                      </span>
                    </div>
                    

                  </div>
                </div>
              `);
            } else {
              // No image - show compact info
              infoWindow.current.setContent(`
                <div style="width: 320px; padding: 0; margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
                  <div style="background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);">
                    <div style="padding: 20px; text-align: center; position: relative;">
                      <div style="height: 120px; background: #f3f4f6; border-radius: 6px; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; cursor: pointer;" onclick="window.handleImageClick && window.handleImageClick('${location.id}')">
                        <span style="color: #9ca3af; font-size: 14px;">No image available</span>
                      </div>
                      
                      <!-- Category and Status -->
                      <div style="display: flex; flex-direction: column; gap: 8px; align-items: center;">
                        <div style="display: flex; align-items: center; gap: 6px; padding: 6px 12px; background: #f9fafb; border-radius: 16px; border: 1px solid #e5e7eb;">
                          <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${categoryColor}; border: 1px solid #ffffff;"></div>
                          <span style="font-size: 13px; font-weight: 600; color: #111827; text-transform: capitalize;">
                            ${(location.category || "Unknown").replace(/_/g, ' ').toLowerCase()}
                          </span>
                        </div>
                        
                        <div style="padding: 6px 12px; background: #374151; border-radius: 16px;">
                          <span style="font-size: 11px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.5px;">
                            ${location.status || "Unknown"}
                          </span>
                        </div>
                        

                      </div>
                    </div>
                  </div>
                </div>
              `);
            }
            
            infoWindow.current.open(map, marker);
          }
        });

        marker.addListener("mouseout", () => {
          if (infoWindow.current) infoWindow.current.close();
        });

        // Add click event to navigate to issue details
        if (onMarkerClick) {
          marker.addListener("click", () => {
            onMarkerClick(location);
          });
        }

        markers.current.push(marker);
        bounds.extend(position);
      }
    });

    // Fit all markers or set default Gurugram view
    if (markers.current.length > 0) {
      map.fitBounds(bounds);
      google.maps.event.addListenerOnce(map, "idle", () => {
        if (map.getZoom() > 15) map.setZoom(15);
      });
    } else {
      // If no markers, keep focused on Gurugram
      map.setCenter({ lat: 28.4595, lng: 77.0266 });
      map.setZoom(12);
    }
  };

  // -------- Initialize Map ----------
  useEffect(() => {
    if (!window.google || !mapRef.current) return;

    const map = new google.maps.Map(mapRef.current, {
      center: { lat: 28.4595, lng: 77.0266 }, // Gurugram center
      zoom: 12, // Slightly more zoomed in for better Gurugram focus
      mapTypeControl: false,
      fullscreenControl: true,
      streetViewControl: false,
      draggable: !isDisabled,
      zoomControl: !isDisabled,
      scrollwheel: !isDisabled,
      disableDoubleClickZoom: isDisabled,
      // Set bounds to keep focus on Gurugram area
      restriction: {
        latLngBounds: {
          north: 28.6,
          south: 28.3,
          west: 76.8,
          east: 77.3,
        },
        strictBounds: false,
      },
    });
    mapInstance.current = map;

    infoWindow.current = new google.maps.InfoWindow();
    google.maps.event.addListener(infoWindow.current, "domready", () => {
        const closeBtn = document.querySelector(".gm-ui-hover-effect") as HTMLElement;
        if (closeBtn) closeBtn.style.display = "none";
      });
    placesService.current = new google.maps.places.PlacesService(map);
  }, []);

  // -------- Handle Map Visibility and Refresh ----------
  useEffect(() => {
    if (!mapInstance.current) return;

    // Function to refresh map when it becomes visible
    const refreshMap = () => {
      if (mapInstance.current) {
        // Trigger a resize event to refresh the map
        google.maps.event.trigger(mapInstance.current, 'resize');
        
        // Re-center the map
        mapInstance.current.setCenter({ lat: 28.4595, lng: 77.0266 });
        mapInstance.current.setZoom(12);
        
        // Re-add markers if data exists
        if (data.length > 0) {
          addLocationMarkers();
        }
      }
    };

    // Use Intersection Observer to detect when map becomes visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && mapInstance.current) {
            // Small delay to ensure the container is fully rendered
            setTimeout(refreshMap, 100);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (mapRef.current) {
      observer.observe(mapRef.current);
    }

    // Cleanup observer
    return () => {
      if (mapRef.current) {
        observer.unobserve(mapRef.current);
      }
    };
  }, [data]);

  // -------- Force Map Refresh on Mount/Unmount ----------
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapInstance.current) {
        // Force a resize event to ensure map renders properly
        google.maps.event.trigger(mapInstance.current, 'resize');
        
        // Re-center and re-add markers
        mapInstance.current.setCenter({ lat: 28.4595, lng: 77.0266 });
        if (data.length > 0) {
          addLocationMarkers();
        }
      }
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  // -------- Update Markers on Data Change ----------
  useEffect(() => {
    if (mapInstance.current && data.length > 0) {
      addLocationMarkers();
    }
  }, [data, getMarkerColor]);

  return (
    <div className="p-4">
      <div className="rounded-2xl overflow-hidden" style={{ height }}>
        <div ref={mapRef} className="w-full h-full" />
      </div>
    </div>
  );
}